import {
  Table,
  Checkbox,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Paper,
  Box,
  Collapse,
  TablePagination,
  TableSortLabel,
  Button,
  Skeleton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Stack,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import WarningIcon from "@mui/icons-material/Warning";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import CreateIcon from "@mui/icons-material/Create";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import TablePaginationActions from "../../components/TablePaginationAction";
import { useEffect, useRef, useState, Fragment } from "react";
import moment from "moment/moment";
import { exportStyledTableToXLSX } from "../utils/ExportTable";
import {
  useUpdateOrdersManually,
  useGetOrdersForExport,
} from "../../../client/hooks/orders/order.queries";

const OrdersTable = ({
  queryData,
  page,
  setPage,
  refetch,
  setSnackbarMessage,
  setOpenSnackbar,
  isRefetching,
  isLoading,
  filters,
}) => {
  const tableContainerRef = useRef(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [data, setData] = useState({});
  const [editingCells, setEditingCells] = useState({});
  const [editValues, setEditValues] = useState({});
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [bulkEditField, setBulkEditField] = useState("");
  const [bulkEditValue, setBulkEditValue] = useState("");
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [popoverCellKey, setPopoverCellKey] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const updateOrdersMutation = useUpdateOrdersManually();
  const exportOrdersMutation = useGetOrdersForExport();

  const handleExport = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirmExport = async () => {
    setConfirmDialogOpen(false);

    try {
      setSnackbarMessage(
        "Iniciando exportação... Isso pode demorar alguns minutos.",
      );
      setOpenSnackbar(true);

      const exportData = await exportOrdersMutation.mutateAsync(filters);

      if (exportData?.data && exportData.data.length > 0) {
        const allRowsIndexes = exportData.data.map((_, index) => index);
        exportStyledTableToXLSX(exportData.data, allRowsIndexes);

        setSnackbarMessage(
          `Exportação concluída! ${exportData.data.length} pedidos exportados.`,
        );
        setOpenSnackbar(true);
      } else {
        setSnackbarMessage("Nenhum pedido encontrado para exportação.");
        setOpenSnackbar(true);
      }
    } catch (error) {
      console.error("Erro ao exportar pedidos:", error);
      setSnackbarMessage("Erro ao exportar pedidos. Tente novamente.");
      setOpenSnackbar(true);
    }
  };

  const handleCancelExport = () => {
    setConfirmDialogOpen(false);
  };

  useEffect(() => {
    setData(queryData);
  }, [queryData]);

  const handleSort = (key) => {
    const sorted = [...queryData.data].sort((a, b) => {
      let valueA = a;
      let valueB = b;

      const keys = key.split(".");
      keys.forEach((k) => {
        valueA = valueA[k] ?? null;
        valueB = valueB[k] ?? null;
      });

      if (valueA === null && valueB !== null)
        return sortConfig.direction === "asc" ? 1 : -1;
      if (valueB === null && valueA !== null)
        return sortConfig.direction === "asc" ? -1 : 1;
      if (valueA === null && valueB === null) return 0;

      const parseToDate = (value) => {
        if (
          typeof value === "string" &&
          value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/)
        ) {
          const date = new Date(value);
          return isNaN(date.getTime()) ? null : date;
        }
        return null;
      };

      const dateA = parseToDate(valueA);
      const dateB = parseToDate(valueB);

      if (dateA && dateB) {
        return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
      } else if (dateA && !dateB) {
        return sortConfig.direction === "asc" ? -1 : 1;
      } else if (!dateA && dateB) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }

      const parseToNumber = (value) => {
        if (typeof value === "string") {
          const parsedValue = value.replace(/[^\d.-]/g, "");
          const number = parseFloat(parsedValue);
          return isNaN(number) ? value : number;
        }
        return value;
      };

      const parsedA = parseToNumber(valueA);
      const parsedB = parseToNumber(valueB);

      if (typeof parsedA === "number" && typeof parsedB === "number") {
        return sortConfig.direction === "asc"
          ? parsedA - parsedB
          : parsedB - parsedA;
      }

      if (Array.isArray(valueA) && Array.isArray(valueB)) {
        const lengthA = valueA.length;
        const lengthB = valueB.length;
        return sortConfig.direction === "asc"
          ? lengthA - lengthB
          : lengthB - lengthA;
      }

      if (typeof valueA === "string" && typeof valueB === "string") {
        return sortConfig.direction === "asc"
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }

      return 0;
    });

    setData({ ...queryData, data: sorted });
    setSortConfig({
      key,
      direction: sortConfig.direction === "asc" ? "desc" : "asc",
    });
  };

  const handleCopy = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);

    setSnackbarMessage("Texto copiado!");
    setOpenSnackbar(true);
  };

  function parseReducedTitle(value, total = 22) {
    if (value.length > total) {
      value = value.substring(0, total) + "...";
      return value;
    }
    return value;
  }

  const handleChangePage = (event, newPage) => {
    setPage({ page_index: newPage, page_count: page.page_count });
    setExpandedRow(null);
    setTimeout(() => {
      refetch();
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTop = 0;
      }
    }, 100);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage({ page_count: parseInt(event.target.value, 10), page_index: 0 });
    setExpandedRow(null);
    setTimeout(() => {
      refetch();
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTop = 0;
      }
    }, 100);
  };

  const parseToCel = (value) => {
    let result = `R$ ${value}`;
    if (!result.includes(".")) {
      result = `R$ ${value}.00`;
    }
    if (result.split(".")[1].length === 1) {
      result = result.concat("0");
    }
    return result.replace(".", ",");
  };

  const handleRowSelect = (rowIndex) => {
    if (selectedRows.includes(rowIndex)) {
      setSelectedRows(selectedRows.filter((index) => index !== rowIndex));
    } else {
      setSelectedRows([...selectedRows, rowIndex]);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedRows(data?.data?.map((_, index) => index) || []);
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowClick = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  // Função para abrir pedido no marketplace
  const handleOpenMarketplace = (marketPlaceId, marketplace) => {
    if (marketPlaceId) {
      let url;

      if (marketplace?.toLowerCase().includes("shopee")) {
        // URL para Shopee
        url = `https://seller.shopee.com.br/portal/sale/order/detail/${marketPlaceId}`;
      } else {
        // URL padrão para Mercado Livre
        url = `https://www.mercadolivre.com.br/vendasofficial/order/${marketPlaceId}`;
      }

      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  // Campos editáveis
  const editableFields = [
    "discount",
    "freight",
    "sellerFreight",
    "comission",
    "vpc",
    "rmb",
    "txFixa",
    "sellerCoupon",
  ];

  // Funções de edição
  const handleCellEdit = (event, rowIndex, field, value) => {
    const cellKey = `${rowIndex}-${field}`;

    // Fechar popover anterior se houver
    if (popoverCellKey && popoverCellKey !== cellKey) {
      handleCellCancel(null, null);
    }

    setPopoverAnchor(event.currentTarget);
    setPopoverCellKey(cellKey);
    setEditingCells((prev) => ({ ...prev, [cellKey]: true }));
    setEditValues((prev) => ({ ...prev, [cellKey]: value }));
  };

  const handleCellSave = async (rowIndex, field) => {
    const cellKey = `${rowIndex}-${field}`;
    const newValue = parseFloat(editValues[cellKey]) || 0;
    const currentOrder = data.data[rowIndex];

    try {
      // Preparar dados para API
      const updateData = [
        {
          order_id: currentOrder.id,
          changed_attributes: [{ key: field, value: newValue }],
        },
      ];

      // Chamar API
      await updateOrdersMutation.mutateAsync(updateData);

      // Atualizar dados locais apenas após sucesso da API
      const updatedData = { ...data };

      // Criar campos se não existirem
      if (
        !updatedData.data[rowIndex][field] &&
        ["vpc", "rmb", "txFixa", "sellerCoupon"].includes(field)
      ) {
        updatedData.data[rowIndex][field] = 0;
      }
      updatedData.data[rowIndex][field] = newValue;

      setData(updatedData);
      setEditingCells((prev) => ({ ...prev, [cellKey]: false }));
      setPopoverAnchor(null);
      setPopoverCellKey(null);

      setSnackbarMessage(`${field} atualizado com sucesso!`);
      setOpenSnackbar(true);
    } catch (error) {
      console.error("Erro ao atualizar pedido:", error);
      setSnackbarMessage(
        `Erro ao atualizar ${field}: ${error.message || "Erro desconhecido"}`,
      );
      setOpenSnackbar(true);

      // Não limpar o modo de edição em caso de erro
      // Para permitir que o usuário tente novamente
    }
  };

  const handleCellCancel = (rowIndex, field) => {
    const cellKey = `${rowIndex}-${field}`;
    setEditingCells((prev) => ({ ...prev, [cellKey]: false }));
    setEditValues((prev) => {
      const newValues = { ...prev };
      delete newValues[cellKey];
      return newValues;
    });
    setPopoverAnchor(null);
    setPopoverCellKey(null);
  };

  const handleBulkEdit = async () => {
    if (!bulkEditField || !bulkEditValue || selectedRows.length === 0) {
      setSnackbarMessage(
        "Selecione linhas e preencha os campos para edição em massa",
      );
      setOpenSnackbar(true);
      return;
    }

    try {
      // Preparar dados para API
      const updateData = selectedRows.map((rowIndex) => {
        const currentOrder = data.data[rowIndex];
        return {
          order_id: currentOrder.id,
          changed_attributes: [
            { key: bulkEditField, value: parseFloat(bulkEditValue) || 0 },
          ],
        };
      });

      // Chamar API
      await updateOrdersMutation.mutateAsync(updateData);

      // Atualizar dados locais apenas após sucesso da API
      const updatedData = { ...data };
      selectedRows.forEach((rowIndex) => {
        // Criar campos se não existirem
        if (
          !updatedData.data[rowIndex][bulkEditField] &&
          ["vpc", "rmb", "txFixa", "sellerCoupon"].includes(bulkEditField)
        ) {
          updatedData.data[rowIndex][bulkEditField] = 0;
        }
        updatedData.data[rowIndex][bulkEditField] =
          parseFloat(bulkEditValue) || 0;
      });

      setData(updatedData);
      setBulkEditMode(false);
      setBulkEditField("");
      setBulkEditValue("");
      setSelectedRows([]);

      setSnackbarMessage(
        `${selectedRows.length} registro(s) atualizado(s) em massa!`,
      );
      setOpenSnackbar(true);
    } catch (error) {
      console.error("Erro ao atualizar pedidos em massa:", error);
      setSnackbarMessage(
        `Erro ao atualizar registros: ${error.message || "Erro desconhecido"}`,
      );
      setOpenSnackbar(true);

      // Não limpar o estado em caso de erro para permitir nova tentativa
    }
  };

  const handleBulkReview = async () => {
    if (selectedRows.length === 0) {
      setSnackbarMessage("Selecione pelo menos um pedido para revisar");
      setOpenSnackbar(true);
      return;
    }

    try {
      // Preparar dados para API com changed_attributes vazio
      const updateData = selectedRows.map((rowIndex) => {
        const currentOrder = data.data[rowIndex];
        return {
          order_id: currentOrder.id,
          changed_attributes: [],
        };
      });

      // Chamar API
      await updateOrdersMutation.mutateAsync(updateData);

      // Limpar seleção após sucesso
      setSelectedRows([]);

      setSnackbarMessage(
        `${selectedRows.length} pedido(s) enviado(s) para revisão!`,
      );
      setOpenSnackbar(true);
    } catch (error) {
      console.error("Erro ao revisar pedidos:", error);
      setSnackbarMessage(
        `Erro ao revisar pedidos: ${error.message || "Erro desconhecido"}`,
      );
      setOpenSnackbar(true);
    }
  };

  const allRowsSelected =
    data?.data?.length > 0 && selectedRows.length === data?.data?.length;

  const getRowStyle = (flagStatus, index) => {
    const baseStyle = {
      backgroundColor: index % 2 === 0 ? "#ffffff" : "#f2f2f2",
    };

    if (flagStatus === "pending_review") {
      return {
        ...baseStyle,
        backgroundColor: "#fff3cd",
        borderLeft: "4px solid #ffc107",
      };
    }

    return baseStyle;
  };

  const getRowIcon = (flagStatus) => {
    switch (flagStatus) {
      case "pending_review":
        return (
          <Tooltip title="Este pedido ainda pode mudar" arrow>
            <WarningIcon
              sx={{ fontSize: 18, color: "#ff9800", marginLeft: 0.5 }}
            />
          </Tooltip>
        );
      case "pending_process":
        return (
          <Tooltip title="Este pedido ainda pode mudar" arrow>
            <HourglassEmptyIcon
              sx={{ fontSize: 18, color: "#2196f3", marginLeft: 0.5 }}
            />
          </Tooltip>
        );
      default:
        return null;
    }
  };

  // Componente de célula editável
  const renderEditableCell = (value, rowIndex, field, originalCellStyle) => {
    const cellKey = `${rowIndex}-${field}`;
    const isEditing = editingCells[cellKey] && popoverCellKey === cellKey;
    const currentValue = isEditing ? editValues[cellKey] : value;

    const content = (
      <Box
        sx={{
          ...originalCellStyle,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "32px",
          borderRadius: "4px",
          "&:hover": {
            backgroundColor: "rgba(25, 118, 210, 0.05)",
          },
          "&:hover .edit-overlay": {
            opacity: 1,
          },
          "&:hover .cell-text": {
            opacity: 0.3,
          },
        }}
        onClick={(e) =>
          editableFields.includes(field) &&
          handleCellEdit(e, rowIndex, field, value)
        }
        style={{
          cursor: editableFields.includes(field) ? "pointer" : "default",
        }}
      >
        <span
          className="cell-text"
          style={{
            transition: "opacity 0.2s ease-in-out",
            opacity: 1,
            zIndex: 1,
          }}
        >
          {parseToCel(parseFloat(value) || 0)}
        </span>
        {editableFields.includes(field) && (
          <Box
            className="edit-overlay"
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: 0,
              transition: "opacity 0.2s ease-in-out",
              zIndex: 2,
              borderRadius: "4px",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
            }}
          >
            <CreateIcon
              sx={{
                fontSize: 18,
                color: "#ff9800",
              }}
            />
          </Box>
        )}
      </Box>
    );

    return editableFields.includes(field) ? (
      <Tooltip title="Clique para editar" arrow placement="top">
        {content}
      </Tooltip>
    ) : (
      content
    );
  };

  const renderSkeletonRow = () => (
    <TableRow>
      <TableCell padding="checkbox">
        <Skeleton variant="rectangular" width={20} height={20} />
      </TableCell>
      <TableCell>
        <Skeleton variant="rectangular" width={24} height={24} />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={60} />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={30} />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={80} />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={80} />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={100} />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={60} />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={50} />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={50} />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={60} />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={50} />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={50} />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={50} />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={50} />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={50} />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={50} />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={50} />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={50} />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={50} />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={40} />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width={80} />
      </TableCell>
    </TableRow>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 125px)",
        overflow: "hidden",
      }}
    >
      {selectedRows.length > 0 && (
        <Box
          sx={{
            backgroundColor: "#f8f9fa",
            border: "1px solid #e9ecef",
            borderLeft: "4px solid #ff9800",
            borderRadius: "8px",
            padding: 2.5,
            margin: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
          }}
        >
          <Chip
            label={`${selectedRows.length} registro(s) selecionado(s)`}
            sx={{
              backgroundColor: "#ff9800",
              color: "white",
              fontWeight: "500",
              "& .MuiChip-label": {
                fontSize: "13px",
              },
            }}
            size="small"
          />

          {!bulkEditMode ? (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                variant="contained"
                size="small"
                onClick={handleBulkReview}
                disabled={updateOrdersMutation.isPending}
                sx={{
                  backgroundColor: "#17a2b8",
                  color: "white",
                  fontWeight: "500",
                  borderRadius: "6px",
                  textTransform: "none",
                  fontSize: "13px",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#138496",
                    boxShadow: "none",
                  },
                  "&:disabled": {
                    backgroundColor: "#6c757d",
                    color: "white",
                  },
                }}
              >
                {updateOrdersMutation.isPending ? "Processando..." : "Revisar"}
              </Button>

              <Button
                variant="outlined"
                size="small"
                onClick={() => setBulkEditMode(true)}
                sx={{
                  color: "#ff9800",
                  borderColor: "#ff9800",
                  fontWeight: "500",
                  borderRadius: "6px",
                  textTransform: "none",
                  fontSize: "13px",
                  "&:hover": {
                    backgroundColor: "#ff9800",
                    color: "white",
                    borderColor: "#ff9800",
                  },
                  transition: "all 0.2s ease-in-out",
                }}
              >
                Edição em Massa
              </Button>
            </Stack>
          ) : (
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ flexWrap: "wrap", gap: 1.5 }}
            >
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={bulkEditField}
                  onChange={(e) => setBulkEditField(e.target.value)}
                  displayEmpty
                  sx={{
                    fontSize: "13px",
                    backgroundColor: "white",
                    borderRadius: "6px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#dee2e6",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#ff9800",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#ff9800",
                    },
                  }}
                >
                  <MenuItem
                    value=""
                    sx={{ fontSize: "13px", color: "#6c757d" }}
                  >
                    Selecionar campo
                  </MenuItem>
                  <MenuItem value="discount" sx={{ fontSize: "13px" }}>
                    DESC
                  </MenuItem>
                  <MenuItem value="freight" sx={{ fontSize: "13px" }}>
                    FP(-)
                  </MenuItem>
                  <MenuItem value="sellerFreight" sx={{ fontSize: "13px" }}>
                    FG(-)
                  </MenuItem>
                  <MenuItem value="comission" sx={{ fontSize: "13px" }}>
                    COMIS(-)
                  </MenuItem>
                  <MenuItem value="vpc" sx={{ fontSize: "13px" }}>
                    VPC(+)
                  </MenuItem>
                  <MenuItem value="rmb" sx={{ fontSize: "13px" }}>
                    RMB(+)
                  </MenuItem>
                  <MenuItem value="txFixa" sx={{ fontSize: "13px" }}>
                    TX FIXA(-)
                  </MenuItem>
                  <MenuItem value="sellerCoupon" sx={{ fontSize: "13px" }}>
                    SCPN(-)
                  </MenuItem>
                </Select>
              </FormControl>

              {bulkEditField && (
                <TextField
                  size="small"
                  placeholder="Valor"
                  value={bulkEditValue}
                  onChange={(e) => setBulkEditValue(e.target.value)}
                  sx={{
                    "& .MuiInputBase-input": {
                      fontSize: "13px",
                      padding: "8px 12px",
                    },
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                      borderRadius: "6px",
                      "& fieldset": {
                        borderColor: "#dee2e6",
                      },
                      "&:hover fieldset": {
                        borderColor: "#ff9800",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#ff9800",
                      },
                    },
                    minWidth: "120px",
                  }}
                  type="number"
                  step="0.01"
                />
              )}

              <Button
                variant="contained"
                size="small"
                onClick={handleBulkEdit}
                disabled={
                  !bulkEditField ||
                  !bulkEditValue ||
                  updateOrdersMutation.isPending
                }
                sx={{
                  backgroundColor: "#28a745",
                  color: "white",
                  fontWeight: "500",
                  borderRadius: "6px",
                  textTransform: "none",
                  fontSize: "13px",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#218838",
                    boxShadow: "none",
                  },
                  "&:disabled": {
                    backgroundColor: "#6c757d",
                    color: "white",
                  },
                }}
              >
                {updateOrdersMutation.isPending ? "Salvando..." : "Aplicar"}
              </Button>

              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setBulkEditMode(false);
                  setBulkEditField("");
                  setBulkEditValue("");
                }}
                sx={{
                  color: "#6c757d",
                  borderColor: "#dee2e6",
                  fontWeight: "500",
                  borderRadius: "6px",
                  textTransform: "none",
                  fontSize: "13px",
                  "&:hover": {
                    backgroundColor: "#f8f9fa",
                    borderColor: "#adb5bd",
                    color: "#495057",
                  },
                }}
              >
                Cancelar
              </Button>
            </Stack>
          )}
        </Box>
      )}
      <TableContainer
        ref={tableContainerRef}
        component={Paper}
        sx={{
          cursor: "default",
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          width: "100%",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          overflowY: "auto",
        }}
      >
        <Table aria-label="orders table">
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: "#2c3e50",
                color: "white",
                position: "sticky",
                top: 0,
                zIndex: 2,
                height: "56px",
              }}
            >
              <TableCell padding="checkbox">
                <Checkbox
                  color="primary"
                  size="small"
                  checked={allRowsSelected}
                  indeterminate={selectedRows.length > 0 && !allRowsSelected}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell sx={{ ...headerCellStyle, width: "15px" }}></TableCell>
              <TableCell
                align="left"
                sx={{ ...headerCellStyle, width: "60px", maxWidth: "60px" }}
              >
                <TableSortLabel
                  sx={{
                    "&.Mui-active": { color: "#FFF" },
                    "& .MuiTableSortLabel-icon": {
                      color: "#FFF !important",
                      fontSize: "14px",
                    },
                    ":hover": { color: "#FFF" },
                  }}
                  active={sortConfig.key === "partnerId"}
                  direction={sortConfig.direction}
                  onClick={() => handleSort("partnerId")}
                >
                  PEDIDO
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...headerCellStyle, maxWidth: "45px" }}>
                <TableSortLabel
                  sx={{
                    "&.Mui-active": { color: "#FFF" },
                    "& .MuiTableSortLabel-icon": {
                      color: "#FFF !important",
                      fontSize: "14px",
                    },
                    ":hover": { color: "#FFF" },
                  }}
                  active={sortConfig.key === "items"}
                  direction={sortConfig.direction}
                  onClick={() => handleSort("items")}
                >
                  ITENS
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{
                  ...headerCellStyle,
                  maxWidth: "50px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                <TableSortLabel
                  sx={{
                    "&.Mui-active": { color: "#FFF" },
                    "& .MuiTableSortLabel-icon": {
                      color: "#FFF !important",
                      fontSize: "14px",
                    },
                    ":hover": { color: "#FFF" },
                  }}
                  active={sortConfig.key === "marketPlace"}
                  direction={sortConfig.direction}
                  onClick={() => handleSort("marketPlace")}
                >
                  <Tooltip title={"MARKETPLACE (CANAL)"} arrow>
                    <span
                      style={{
                        maxWidth: "60px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      MARKETPLACE
                    </span>
                  </Tooltip>
                </TableSortLabel>
              </TableCell>
              <TableCell sx={headerCellStyle}>
                <TableSortLabel
                  sx={{
                    "&.Mui-active": { color: "#FFF" },
                    "& .MuiTableSortLabel-icon": {
                      color: "#FFF !important",
                      fontSize: "14px",
                    },
                    ":hover": { color: "#FFF" },
                  }}
                  active={sortConfig.key === "status"}
                  direction={sortConfig.direction}
                  onClick={() => handleSort("status")}
                >
                  STATUS
                </TableSortLabel>
              </TableCell>
              <TableCell sx={headerCellStyle}>
                <TableSortLabel
                  sx={{
                    "&.Mui-active": { color: "#FFF" },
                    "& .MuiTableSortLabel-icon": {
                      color: "#FFF !important",
                      fontSize: "14px",
                    },
                    ":hover": { color: "#FFF" },
                  }}
                  active={sortConfig.key === "marketPlaceId"}
                  direction={sortConfig.direction}
                  onClick={() => handleSort("marketPlaceId")}
                >
                  <Tooltip title={"PEDIDO LOJA (MARKETPLACE ID)"} arrow>
                    <span
                      style={{
                        maxWidth: "60px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      PEDIDO-LOJA
                    </span>
                  </Tooltip>
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{
                  ...headerCellStyle,
                }}
              >
                <TableSortLabel
                  sx={{
                    "&.Mui-active": { color: "#FFF" },
                    "& .MuiTableSortLabel-icon": {
                      color: "#FFF !important",
                      fontSize: "14px",
                    },
                    ":hover": { color: "#FFF" },
                  }}
                  active={sortConfig.key === "total"}
                  direction={sortConfig.direction}
                  onClick={() => handleSort("total")}
                >
                  <Tooltip title={"TOTAL DA VENDA"} arrow>
                    <span
                      style={{
                        maxWidth: "60px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      TOTAL DA VENDA
                    </span>
                  </Tooltip>
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="left"
                sx={{ ...headerCellStyle, textAlign: "center" }}
              >
                DESC
              </TableCell>
              <TableCell
                align="left"
                sx={{ ...headerCellStyle, textAlign: "center" }}
              >
                FP(-)
              </TableCell>
              <TableCell
                align="left"
                sx={{
                  ...headerCellStyle,
                  maxWidth: "35px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                <Tooltip title={"TOTAL DO PRODUTO"} arrow>
                  <span>TOTAL DO PRODUTO</span>
                </Tooltip>
              </TableCell>
              <TableCell
                align="left"
                sx={{ ...headerCellStyle, textAlign: "center" }}
              >
                FG(-)
              </TableCell>
              <TableCell
                align="left"
                sx={{ ...headerCellStyle, textAlign: "center" }}
              >
                CMV(-)
              </TableCell>
              <TableCell
                align="left"
                sx={{ ...headerCellStyle, textAlign: "center" }}
              >
                COMIS(-)
              </TableCell>
              <TableCell
                align="left"
                sx={{ ...headerCellStyle, textAlign: "center" }}
              >
                IMP(-)
              </TableCell>
              <TableCell
                align="left"
                sx={{ ...headerCellStyle, textAlign: "center" }}
              >
                VPC(+)
              </TableCell>
              <TableCell
                align="left"
                sx={{ ...headerCellStyle, textAlign: "center" }}
              >
                RMB(+)
              </TableCell>
              <TableCell
                align="left"
                sx={{ ...headerCellStyle, textAlign: "center" }}
              >
                TX FIXA(-)
              </TableCell>
              <TableCell
                align="left"
                sx={{ ...headerCellStyle, textAlign: "center" }}
              >
                SCPN(-)
              </TableCell>
              <TableCell
                align="left"
                sx={{ ...headerCellStyle, textAlign: "center" }}
              >
                =MAR
              </TableCell>
              <TableCell
                align="left"
                sx={{ ...headerCellStyle, textAlign: "center" }}
              >
                MAR%
              </TableCell>
              <TableCell align="left" sx={headerCellStyle}>
                <TableSortLabel
                  sx={{
                    "&.Mui-active": { color: "#FFF" },
                    "& .MuiTableSortLabel-icon": {
                      color: "#FFF !important",
                      fontSize: "14px",
                    },
                    ":hover": { color: "#FFF" },
                  }}
                  active={sortConfig.key === "shipping.state"}
                  direction={sortConfig.direction}
                  onClick={() => handleSort("shipping.state")}
                >
                  <span>UF</span>
                </TableSortLabel>
              </TableCell>
              <TableCell align="left" sx={headerCellStyle}>
                <TableSortLabel
                  sx={{
                    "&.Mui-active": { color: "#FFF" },
                    "& .MuiTableSortLabel-icon": {
                      color: "#FFF !important",
                      fontSize: "14px",
                    },
                    ":hover": { color: "#FFF" },
                  }}
                  active={sortConfig.key === "createdAt"}
                  direction={sortConfig.direction}
                  onClick={() => handleSort("createdAt")}
                >
                  <Tooltip title={"DATA DE CRIAÇÃO DO PEDIDO"} arrow>
                    <span
                      style={{
                        maxWidth: "60px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      DATA DE CRIAÇÃO DO PEDIDO
                    </span>
                  </Tooltip>
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.data?.map((value, index) => (
              <Fragment key={value?.id || index}>
                {value?.flag_status === "pending_process" ? (
                  renderSkeletonRow()
                ) : (
                  <TableRow sx={getRowStyle(value?.flag_status, index)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        size="small"
                        checked={selectedRows.includes(index)}
                        onChange={() => handleRowSelect(index)}
                      />
                    </TableCell>
                    <TableCell
                      onClick={() => handleRowClick(index)}
                      sx={{ ...cellStyle }}
                    >
                      <IconButton size="small">
                        {expandedRow === index ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </IconButton>
                      {getRowIcon(value?.flag_status)}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...cellStyle,
                        width: "20px",
                        maxWidth: "20px",
                        textAlign: "left",
                      }}
                    >
                      {value?.partnerId}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...cellStyle,
                        width: "30px",
                        textAlign: "left",
                        paddingLeft: "15px",
                      }}
                    >
                      {value?.items.length}
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, maxWidth: "20px" }}>
                      <Tooltip title={value?.marketPlace} arrow>
                        <span onClick={() => handleCopy(value?.marketPlace)}>
                          {value?.marketPlace}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      sx={{ ...cellStyle, maxWidth: "20px", width: "20px" }}
                    >
                      <Tooltip title={value?.status} arrow>
                        <span onClick={() => handleCopy(value?.status)}>
                          {value?.status}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      sx={{ ...cellStyle, maxWidth: "60px", width: "60px" }}
                    >
                      <Tooltip
                        title={`Abrir pedido ${value?.marketPlaceId || value?.id} no ${value?.marketPlace}`}
                        arrow
                      >
                        <Box
                          sx={{
                            position: "relative",
                            cursor: "pointer",
                            padding: "4px",
                            borderRadius: "4px",
                            transition: "all 0.2s ease-in-out",
                            "&:hover": {
                              backgroundColor: "rgba(25, 118, 210, 0.05)",
                            },
                            "&:hover .cell-text": {
                              opacity: 0.3,
                            },
                            "&:hover .edit-overlay": {
                              opacity: 1,
                            },
                          }}
                          onClick={() =>
                            handleOpenMarketplace(
                              value?.marketPlaceId || value?.id,
                              value?.marketPlace,
                            )
                          }
                        >
                          <span
                            className="cell-text"
                            style={{
                              transition: "opacity 0.2s ease-in-out",
                              opacity: 1,
                              zIndex: 1,
                              maxWidth: "60px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              display: "block",
                            }}
                          >
                            {parseReducedTitle(
                              value?.marketPlaceId || value?.id,
                            )}
                          </span>
                          <Box
                            className="edit-overlay"
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              opacity: 0,
                              transition: "opacity 0.2s ease-in-out",
                              zIndex: 2,
                              borderRadius: "4px",
                              backgroundColor: "rgba(255, 255, 255, 0.9)",
                            }}
                          >
                            <OpenInNewIcon
                              sx={{
                                fontSize: 18,
                                color: "#1976d2",
                              }}
                            />
                          </Box>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      onClick={() => handleCopy(value?.total)}
                      sx={{ ...cellStyle, color: "blue", width: "60px" }}
                    >
                      {parseToCel(value?.total)}
                    </TableCell>
                    <TableCell sx={cellStyle}>
                      {renderEditableCell(
                        value?.discount,
                        index,
                        "discount",
                        cellStyle,
                      )}
                    </TableCell>
                    <TableCell sx={cellStyle}>
                      {renderEditableCell(
                        value?.freight,
                        index,
                        "freight",
                        cellStyle,
                      )}
                    </TableCell>
                    <TableCell
                      sx={{ ...cellStyle, color: "blue", width: "40px" }}
                    >
                      <Tooltip
                        title={`Total Produto: ${parseToCel(value?.totalProduto?.toFixed(2))}`}
                        arrow
                      >
                        <span
                          onClick={() =>
                            handleCopy(value?.totalProduto?.toFixed(2))
                          }
                        >
                          {parseToCel(value?.totalProduto?.toFixed(2))}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={cellStyle}>
                      {renderEditableCell(
                        value?.sellerFreight,
                        index,
                        "sellerFreight",
                        cellStyle,
                      )}
                    </TableCell>
                    <TableCell
                      sx={{ ...cellStyle, color: "red", textAlign: "center" }}
                    >
                      <span
                        onClick={() => handleCopy(value?.totalCvm?.toFixed(2))}
                      >
                        {parseToCel(value?.totalCvm?.toFixed(2))}
                      </span>
                    </TableCell>
                    <TableCell
                      sx={{ ...cellStyle, color: "red", textAlign: "center" }}
                    >
                      {renderEditableCell(
                        value?.comission || 0,
                        index,
                        "comission",
                        { ...cellStyle, color: "red", textAlign: "center" },
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...cellStyle,
                        color: "purple",
                        textAlign: "center",
                      }}
                    >
                      <Tooltip
                        title={
                          <Box display={"flex"} flexDirection={"column"}>
                            <span>
                              PIS Cofins:{" "}
                              {parseToCel(value?.totalImpPisCofins?.toFixed(2))}
                            </span>
                            <span>
                              ICMS Difal:{" "}
                              {parseToCel(value?.icmsDifal?.toFixed(2) || 0)}
                            </span>
                            <span>
                              ICMS Sku:{" "}
                              {value?.items?.some(
                                (item) => item.icms === "Sku Not Found",
                              )
                                ? "Sku não encontrado"
                                : parseToCel(
                                    (
                                      value?.items
                                        ?.map((item) => item.icms)
                                        .reduce((ac, at) => ac + at, 0) || 0
                                    ).toFixed(2),
                                  )}
                            </span>
                          </Box>
                        }
                        arrow
                      >
                        <span onClick={() => handleCopy("IMP(-)")}>
                          {parseToCel(value?.impostoTotal?.toFixed(2))}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, textAlign: "center" }}>
                      {renderEditableCell(value?.vpc || 0, index, "vpc", {
                        ...cellStyle,
                        textAlign: "center",
                      })}
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, textAlign: "center" }}>
                      {renderEditableCell(value?.rmb || 0, index, "rmb", {
                        ...cellStyle,
                        textAlign: "center",
                      })}
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, textAlign: "center" }}>
                      {renderEditableCell(value?.txFixa || 0, index, "txFixa", {
                        ...cellStyle,
                        textAlign: "center",
                      })}
                    </TableCell>
                    <TableCell sx={{ ...cellStyle, textAlign: "center" }}>
                      {renderEditableCell(
                        value?.sellerCoupon || 0,
                        index,
                        "sellerCoupon",
                        { ...cellStyle, textAlign: "center" },
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...cellStyle,
                        color:
                          value?.percentMar > 0
                            ? value?.percentMar < 5
                              ? "black"
                              : value?.percentMar > 10
                                ? "blue"
                                : "blue"
                            : "red",
                        background:
                          value?.percentMar > 0
                            ? value?.percentMar < 5
                              ? "#f2cea7"
                              : value?.percentMar > 10
                                ? "#deeeff"
                                : "#caf5c1"
                            : "#efb5b9",
                        textAlign: "center",
                      }}
                    >
                      <span onClick={() => handleCopy(value?.equalMar)}>
                        {parseToCel(value?.equalMar?.toFixed(2))}
                      </span>
                    </TableCell>
                    <TableCell
                      sx={{
                        ...cellStyle,
                        color:
                          value?.percentMar > 0
                            ? value?.percentMar < 5
                              ? "black"
                              : value?.percentMar > 10
                                ? "blue"
                                : "blue"
                            : "red",
                        background:
                          value?.percentMar > 0
                            ? value?.percentMar < 5
                              ? "#f2cea7"
                              : value?.percentMar > 10
                                ? "#deeeff"
                                : "#caf5c1"
                            : "#efb5b9",
                        textAlign: "center",
                      }}
                    >
                      <span onClick={() => handleCopy(value?.percentMar)}>
                        {value?.percentMar?.toFixed(2)}%
                      </span>
                    </TableCell>
                    <TableCell
                      sx={{
                        ...cellStyle,
                        textAlign: "left",
                      }}
                    >
                      <span onClick={() => handleCopy(value?.shipping?.state)}>
                        {value?.shipping?.state}
                      </span>
                    </TableCell>
                    <TableCell
                      sx={{
                        ...cellStyle,
                        textAlign: "left",
                        width: "80px",
                        maxWidth: "80px",
                      }}
                    >
                      <Tooltip
                        title={`Data da Criação: ${moment(value?.createdAt).format("DD/MM/YYYY HH:mm")}`}
                        arrow
                      >
                        <span
                          onClick={() =>
                            handleCopy(
                              moment(value?.createdAt).format(
                                "DD/MM/YYYY HH:mm:ss",
                              ),
                            )
                          }
                        >
                          {moment(value?.createdAt).format(
                            "DD/MM/YYYY HH:mm:ss",
                          )}
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )}
                {expandedRow === index &&
                  value?.flag_status !== "pending_process" && (
                    <TableRow>
                      <TableCell colSpan={19}>
                        <Collapse
                          in={expandedRow === index}
                          timeout={{ enter: 300, exit: 300 }}
                          sx={{
                            animation:
                              expandedRow === index
                                ? "fadeIn 300ms ease-in-out"
                                : "fadeOut 300ms ease-in-out",
                            "@keyframes fadeIn": {
                              from: {
                                opacity: 0,
                                transform: "translateY(-10px)",
                              },
                              to: { opacity: 1, transform: "translateY(0)" },
                            },
                            "@keyframes fadeOut": {
                              from: { opacity: 1, transform: "translateY(0)" },
                              to: {
                                opacity: 0,
                                transform: "translateY(-10px)",
                              },
                            },
                            padding: 2,
                            width: "100%",
                            backgroundColor: "#f9f9f9",
                            borderRadius: "5px",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                          }}
                        >
                          <Box
                            display={"flex"}
                            justifyContent={"center"}
                            justifyItems={"center"}
                          >
                            <Table sx={{ width: "100%", margin: "auto" }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell>SKU</TableCell>
                                  <TableCell>Produto</TableCell>
                                  <TableCell>Quantidade</TableCell>
                                  <TableCell>Valor Unitário</TableCell>
                                  <TableCell>Valor Bruto</TableCell>
                                  <TableCell>Desconto Produto</TableCell>
                                  <TableCell>CMV</TableCell>
                                  <TableCell>ICMS Sku</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {value.items?.map((item, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell>{item.sku.partnerId}</TableCell>
                                    <TableCell>{item.sku.title}</TableCell>
                                    <TableCell>{item.amount}</TableCell>
                                    <TableCell>{`${parseToCel(item.unit.toFixed(2))}`}</TableCell>
                                    <TableCell>{`${parseToCel(item.gross.toFixed(2))}`}</TableCell>
                                    <TableCell>{`${parseToCel(item.discount.toFixed(2))}`}</TableCell>
                                    <TableCell>{`${parseToCel(item?.calculatedCvm?.toFixed(2) || 0)}`}</TableCell>
                                    {/* TODO
                                  Ajustar para quando for uma string ele não dar erro e mostrar visualmente que tem que ajustar
                                  */}
                                    <TableCell>
                                      {item.icms === "Sku Not Found"
                                        ? "Sku não encontrado"
                                        : parseToCel(
                                            item?.icms?.toFixed(2) || 0,
                                          )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Popover para edição */}
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={() => handleCellCancel(null, null)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        sx={{
          "& .MuiPopover-paper": {
            padding: 1,
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            borderRadius: "8px",
            border: "1px solid #e0e0e0",
            minWidth: "200px",
          },
        }}
      >
        {popoverCellKey && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              padding: "8px",
            }}
          >
            <TextField
              size="small"
              value={editValues[popoverCellKey] || ""}
              onChange={(e) =>
                setEditValues((prev) => ({
                  ...prev,
                  [popoverCellKey]: e.target.value,
                }))
              }
              sx={{
                "& .MuiInputBase-input": {
                  fontSize: "13px",
                  padding: "8px 12px",
                },
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#1976d2",
                  },
                },
              }}
              type="number"
              step="0.01"
              autoFocus
              placeholder="Valor"
            />
            <IconButton
              size="small"
              onClick={() => {
                const [rowIndex, field] = popoverCellKey.split("-");
                handleCellSave(parseInt(rowIndex), field);
              }}
              disabled={updateOrdersMutation.isPending}
              sx={{
                color: "#4caf50",
                backgroundColor: "rgba(76, 175, 80, 0.1)",
                "&:hover": {
                  backgroundColor: "rgba(76, 175, 80, 0.2)",
                },
              }}
            >
              <SaveIcon sx={{ fontSize: "16px" }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleCellCancel(null, null)}
              sx={{
                color: "#f44336",
                backgroundColor: "rgba(244, 67, 54, 0.1)",
                "&:hover": {
                  backgroundColor: "rgba(244, 67, 54, 0.2)",
                },
              }}
            >
              <CancelIcon sx={{ fontSize: "16px" }} />
            </IconButton>
          </Box>
        )}
      </Popover>
      <Box
        sx={{
          backgroundColor: "#ffffff",
          borderTop: "1px solid #dcdcdc",
          boxShadow: "0px -2px 4px rgba(0,0,0,0.1)",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          sx={{
            margin: "auto auto auto 15px",
            textAlign: "center",
            fontSize: "12px",
            fontWeight: 600,
            height: "30px",
            borderRadius: "4px",
            border: "1px solid",
          }}
          onClick={handleExport}
          disabled={exportOrdersMutation.isPending}
        >
          {exportOrdersMutation.isPending ? "EXPORTANDO..." : "EXPORTAR"}
        </Button>
        <TablePagination
          component="div"
          rowsPerPageOptions={[20, 50, 100]}
          count={data?.total || 0}
          rowsPerPage={page.page_count}
          page={page.page_index}
          slotProps={{
            select: {
              inputProps: { "aria-label": "rows per page" },
              native: true,
            },
          }}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
          disabled={isRefetching || isLoading}
        />
      </Box>

      {/* Modal de Confirmação de Exportação */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelExport}
        aria-labelledby="export-dialog-title"
        aria-describedby="export-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "8px",
            minWidth: "400px",
          },
        }}
      >
        <DialogTitle
          id="export-dialog-title"
          sx={{
            fontWeight: "bold",
            fontSize: "18px",
            color: "#2c3e50",
            textAlign: "center",
            paddingBottom: "8px",
          }}
        >
          Confirmar Exportação
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="export-dialog-description"
            sx={{
              fontSize: "14px",
              color: "#5a6c7d",
              textAlign: "center",
              lineHeight: 1.6,
            }}
          >
            Tem certeza que deseja exportar todos os pedidos?
            <br />
            Esta operação pode demorar alguns minutos dependendo da quantidade
            de dados.
            <br />
            <strong>
              Todos os filtros aplicados serão considerados na exportação.
            </strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{ justifyContent: "center", paddingTop: "16px", gap: 2 }}
        >
          <Button
            onClick={handleCancelExport}
            variant="outlined"
            sx={{
              color: "#6c757d",
              borderColor: "#dee2e6",
              fontWeight: "500",
              borderRadius: "6px",
              textTransform: "none",
              fontSize: "14px",
              minWidth: "100px",
              "&:hover": {
                backgroundColor: "#f8f9fa",
                borderColor: "#adb5bd",
                color: "#495057",
              },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmExport}
            variant="contained"
            disabled={exportOrdersMutation.isPending}
            sx={{
              backgroundColor: "#ff9800",
              color: "white",
              fontWeight: "500",
              borderRadius: "6px",
              textTransform: "none",
              fontSize: "14px",
              boxShadow: "none",
              minWidth: "100px",
              "&:hover": {
                backgroundColor: "#e68900",
                boxShadow: "0 2px 8px rgba(255, 152, 0, 0.3)",
              },
              "&:disabled": {
                backgroundColor: "#6c757d",
                color: "white",
              },
            }}
          >
            {exportOrdersMutation.isPending ? "Exportando..." : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const headerCellStyle = {
  fontWeight: "bold",
  fontSize: "12px",
  color: "#ffffff",
  textAlign: "left",
  backgroundColor: "#34495e",
  padding: "8px",
};

const cellStyle = {
  fontSize: "12px",
  padding: "8px",
  color: "#2c3e50",
  cursor: "pointer",
  textAlign: "left",
  maxWidth: "120px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  "&:hover": {
    backgroundColor: "#ecf0f1",
  },
};

export default OrdersTable;
